
import { useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts'
import { WebAnalytics } from '@/lib/analytics'
import { EVENT_APP_INSTALLED } from '@/lib/analyticsEvents'
import { getUtmsFromCurrentUrl } from '@/lib/utm'

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = useLocation().pathname
  const [searchParams] = useSearchParams()
  const { user } = useAuth()

  // Initialize analytics on mount
  useEffect(() => {
    WebAnalytics.init()

    // Capture acquisition source from URL parameters
    WebAnalytics.captureAcquisitionSource()

    // First-ever launch detection — fires once per install/device.
    const firstLaunchKey = 'gt_first_launch_v1'
    if (!localStorage.getItem(firstLaunchKey)) {
      WebAnalytics.track(EVENT_APP_INSTALLED)
      localStorage.setItem(firstLaunchKey, new Date().toISOString())
    }
  }, [])

  // Persist UTM + share-referral params (sr_type/sr_id) from landing URLs for signup attribution
  useEffect(() => {
    getUtmsFromCurrentUrl();
    // Re-capture first-touch after client-side redirects (e.g. /s/{code} → destination with creator UTMs)
    WebAnalytics.captureAcquisitionSource();
  }, [searchParams]);

  // Track page views on route changes
  useEffect(() => {
    if (!pathname) return

    // Get page name from pathname
    const pageName = pathname === '/' ? 'Home' : pathname.slice(1).replace(/\//g, ' > ')

    // Get UTM parameters from URL
    const utmParams = {
      utm_source: searchParams?.get('utm_source') || undefined,
      utm_medium: searchParams?.get('utm_medium') || undefined,
      utm_campaign: searchParams?.get('utm_campaign') || undefined,
      utm_content: searchParams?.get('utm_content') || undefined,
      utm_term: searchParams?.get('utm_term') || undefined
    }

    // Filter out undefined values
    const filteredParams = Object.fromEntries(
      Object.entries(utmParams).filter(([_unused, v]) => v !== undefined)
    )

    // Get stored acquisition source
    const acquisitionSource = WebAnalytics.getAcquisitionSource()

    const pageMetadata = { user_state: user ? 'logged_in' : 'logged_out' }

    // Track visit content event
    WebAnalytics.trackVisitContent(pageName, {
      path: pathname,
      ...filteredParams,
      ...pageMetadata,
      // Include original acquisition source for attribution
      ...(acquisitionSource && {
        original_source: acquisitionSource.utm_source,
        original_medium: acquisitionSource.utm_medium,
        original_campaign: acquisitionSource.utm_campaign
      })
    })
  }, [pathname, searchParams, user])

  return <>{children}</>
}
